import React, { useState, useEffect } from 'react';
import { Box, Container, Typography, useTheme, useMediaQuery, Button, Menu, MenuItem, IconButton } from '@mui/material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ContactBanner, SiteFooter } from './Home';
import MenuIcon from '@mui/icons-material/Menu';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import axios from 'axios';

// Interfaces TypeScript
interface BlogArticle {
  id: number;
  title: string;
  excerpt: string;
  content?: string;
  image_url: string;
  alt_text?: string;
  author: string;
  is_published: boolean;
  is_featured: boolean;
  order_index: number;
  created_at: string;
  updated_at: string;
  category?: string;
}

interface SchoolNews {
  id: number;
  title: string;
  description: string;
  image_url: string;
  alt_text?: string;
  order_index: number;
  created_at: string;
  updated_at: string;
}

// Composant de navigation avec onglets (réutilisé de Home.tsx)
const NavigationBar = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileMenuAnchor, setMobileMenuAnchor] = useState<null | HTMLElement>(null);
  const [scolariteMenuAnchor, setScolariteMenuAnchor] = useState<null | HTMLElement>(null);

  const handleMobileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMobileMenuAnchor(event.currentTarget);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuAnchor(null);
  };

  const handleScolariteMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setScolariteMenuAnchor(event.currentTarget);
  };

  const handleScolariteMenuClose = () => {
    setScolariteMenuAnchor(null);
  };

  const handleContactsClick = () => {
    const footerElement = document.getElementById('footer');
    if (footerElement) {
      footerElement.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const navigationItems = [
    { label: 'ACCUEIL', path: '/' },
    { label: 'PRESENTATION', path: '/presentation' },
    { 
      label: 'SCOLARITE', 
      path: '/scolarite',
      hasSubmenu: true,
      submenu: [
        { label: 'MATERNELLE', path: '/scolarite' },
        { label: 'CRÈCHE/GARDERIE', path: '/scolarite-maternel' }
      ]
    },
    { label: 'NOS ACTIVITES', path: '/activites' },
    { label: 'BLOG', path: '/blog' },
    { label: 'CONTACTS', path: '#footer', isScroll: true },
    { label: 'CONNEXION', path: '/login' },
  ];

  return (
    <Box 
      position="absolute" 
      sx={{ 
        bgcolor: 'transparent',
        boxShadow: 'none',
        zIndex: 10,
        top: '60px',
        width: '100%'
      }}
    >
      <Container maxWidth="lg">
        <Box sx={{ 
          display: 'flex',
          justifyContent: { xs: 'flex-end', md: 'space-between' },
          alignItems: 'center',
          py: { xs: 1, md: 2 },
          px: { xs: 2, md: 0 },
          minHeight: { xs: 56, md: 64 }
        }}>
          {/* Logo - Masqué sur mobile */}
          <Box sx={{ 
            display: { xs: 'none', md: 'flex' },
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Box sx={{
              width: 60,
              height: 60,
              border: '2px solid #87CEEB',
              borderRadius: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'white',
              overflow: 'hidden',
              p: 0.5,
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
            }}>
              <img
                src="/img/pages/logologo.webp"
                alt="La Maison des Enfants La petite Académie"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                  display: 'block'
                }}
              />
            </Box>
          </Box>

          {/* Navigation Desktop */}
          {!isMobile && (
            <Box sx={{ 
              display: 'flex', 
              gap: { md: 2, lg: 3 },
              alignItems: 'center'
            }}>
              {navigationItems.map((item) => (
                <Box key={item.label}>
                  {item.hasSubmenu ? (
                    <>
                      <Typography
                        onClick={handleScolariteMenuOpen}
                        sx={{
                          color: 'white',
                          fontWeight: 600,
                          fontSize: { md: 12, lg: 14 },
                          textTransform: 'uppercase',
                          letterSpacing: 0.5,
                          px: { md: 1.5, lg: 2 },
                          py: 1,
                          borderBottom: '2px solid transparent',
                          cursor: 'pointer',
                          '&:hover': {
                            color: '#FF9800',
                            borderBottom: '2px solid #FF9800',
                          }
                        }}
                      >
                        {item.label}
                      </Typography>
                      <Menu
                        anchorEl={scolariteMenuAnchor}
                        open={Boolean(scolariteMenuAnchor)}
                        onClose={handleScolariteMenuClose}
                        sx={{
                          '& .MuiPaper-root': {
                            bgcolor: 'rgba(23, 128, 194, 0.95)',
                            color: 'white',
                            mt: 1,
                            backdropFilter: 'blur(10px)',
                            minWidth: 200
                          }
                        }}
                      >
                        {item.submenu?.map((subItem) => (
                          <MenuItem
                            key={subItem.label}
                            component={RouterLink}
                            to={subItem.path}
                            onClick={handleScolariteMenuClose}
                            sx={{
                              color: 'white',
                              fontWeight: 600,
                              fontSize: 14,
                              textTransform: 'uppercase',
                              '&:hover': {
                                bgcolor: 'rgba(255, 255, 255, 0.1)',
                              }
                            }}
                          >
                            {subItem.label}
                          </MenuItem>
                        ))}
                      </Menu>
                    </>
                  ) : item.isScroll ? (
                    <Typography
                      onClick={handleContactsClick}
                      sx={{
                        color: 'white',
                        fontWeight: 600,
                        fontSize: { md: 12, lg: 14 },
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        px: { md: 1.5, lg: 2 },
                        py: 1,
                        borderBottom: '2px solid transparent',
                        cursor: 'pointer',
                        '&:hover': {
                          color: '#FF9800',
                          borderBottom: '2px solid #FF9800',
                        }
                      }}
                    >
                      {item.label}
                    </Typography>
                  ) : (
                    <Typography
                      component={RouterLink}
                      to={item.path}
                      sx={{
                        color: item.label === 'BLOG' ? '#FF9800' : 'white',
                        fontWeight: 600,
                        fontSize: { md: 12, lg: 14 },
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        px: { md: 1.5, lg: 2 },
                        py: 1,
                        borderBottom: item.label === 'BLOG' ? '2px solid #FF9800' : '2px solid transparent',
                        cursor: 'pointer',
                        textDecoration: 'none',
                        '&:hover': {
                          color: '#FF9800',
                          borderBottom: '2px solid #FF9800',
                        }
                      }}
                    >
                      {item.label}
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
          )}

          {/* Menu Mobile */}
          {isMobile && (
            <>
              <IconButton
                color="inherit"
                onClick={handleMobileMenuOpen}
                sx={{ color: 'white' }}
              >
                <MenuIcon />
              </IconButton>
              <Menu
                anchorEl={mobileMenuAnchor}
                open={Boolean(mobileMenuAnchor)}
                onClose={handleMobileMenuClose}
                sx={{
                  '& .MuiPaper-root': {
                    bgcolor: 'rgba(23, 128, 194, 0.95)',
                    color: 'white',
                    mt: 1,
                    backdropFilter: 'blur(10px)'
                  }
                }}
              >
                {navigationItems.map((item) => (
                  <Box key={item.label}>
                    {item.hasSubmenu ? (
                      <>
                        <MenuItem
                          onClick={handleScolariteMenuOpen}
                          sx={{
                            color: 'white',
                            fontWeight: 600,
                            fontSize: 14,
                            textTransform: 'uppercase',
                            '&:hover': {
                              bgcolor: 'rgba(255, 255, 255, 0.1)',
                            }
                          }}
                        >
                          {item.label}
                          <KeyboardArrowDownIcon sx={{ ml: 0.5, fontSize: 16 }} />
                        </MenuItem>
                        <Menu
                          anchorEl={scolariteMenuAnchor}
                          open={Boolean(scolariteMenuAnchor)}
                          onClose={handleScolariteMenuClose}
                          sx={{
                            '& .MuiPaper-root': {
                              bgcolor: 'rgba(23, 128, 194, 0.95)',
                              color: 'white',
                              mt: 1,
                              backdropFilter: 'blur(10px)',
                              minWidth: 200
                            }
                          }}
                        >
                          {item.submenu?.map((subItem) => (
                            <MenuItem
                              key={subItem.label}
                              component={RouterLink}
                              to={subItem.path}
                              onClick={() => {
                                handleScolariteMenuClose();
                                handleMobileMenuClose();
                              }}
                              sx={{
                                color: 'white',
                                fontWeight: 600,
                                fontSize: 14,
                                textTransform: 'uppercase',
                                '&:hover': {
                                  bgcolor: 'rgba(255, 255, 255, 0.1)',
                                }
                              }}
                            >
                              {subItem.label}
                            </MenuItem>
                          ))}
                        </Menu>
                      </>
                    ) : item.isScroll ? (
                      <MenuItem
                        onClick={() => {
                          handleContactsClick();
                          handleMobileMenuClose();
                        }}
                        sx={{
                          color: 'white',
                          fontWeight: 600,
                          fontSize: 14,
                          textTransform: 'uppercase',
                          '&:hover': {
                            bgcolor: 'rgba(255, 255, 255, 0.1)',
                          }
                        }}
                      >
                        {item.label}
                      </MenuItem>
                    ) : (
                      <MenuItem
                        component={RouterLink}
                        to={item.path}
                        onClick={handleMobileMenuClose}
                        sx={{
                          color: item.label === 'BLOG' ? '#FF9800' : 'white',
                          fontWeight: 600,
                          fontSize: 14,
                          textTransform: 'uppercase',
                          '&:hover': {
                            bgcolor: 'rgba(255, 255, 255, 0.1)',
                          }
                        }}
                      >
                        {item.label}
                      </MenuItem>
                    )}
                  </Box>
                ))}
              </Menu>
            </>
          )}
        </Box>
      </Container>
    </Box>
  );
};

// Section Hero pour le Blog
const BlogHeroSection = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <Box sx={{
      position: 'relative',
      width: '100%',
      height: '40vh',
      backgroundImage: 'url(/img/pages/ac.jpg)', // Image de fond du bâtiment
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      overflow: 'hidden'
    }}>
      {/* Overlay sombre semi-transparent */}
      <Box sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        bgcolor: 'rgba(0, 0, 0, 0.6)',
        zIndex: 1
      }} />

      {/* Contenu principal */}
      <Box sx={{
        position: 'relative',
        zIndex: 3,
        textAlign: 'center',
        color: 'white'
      }}>
        <Typography
          variant="h1"
          sx={{
            color: 'white',
            fontWeight: 'bold',
            fontSize: { xs: 32, md: 48, lg: 64 },
            textShadow: '2px 2px 4px rgba(0,0,0,0.8)',
            mb: 2,
            textTransform: 'uppercase',
            letterSpacing: 2,
            fontFamily: 'Arial, sans-serif'
          }}
        >
          BLOG
        </Typography>
        
        <Typography
          variant="h4"
          sx={{
            color: 'white',
            fontWeight: 400,
            fontSize: { xs: 16, md: 20, lg: 24 },
            textShadow: '2px 2px 4px rgba(0,0,0,0.8)',
            fontFamily: 'Arial, sans-serif'
          }}
        >
          Découvrez nos articles sur l'éducation et le développement des enfants
        </Typography>
      </Box>
    </Box>
  );
};

// Section des articles de blog avec données dynamiques
const BlogArticlesSection = ({ articles }: { articles: BlogArticle[] }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();

  // Fonction pour obtenir les catégories uniques
  const getUniqueCategories = (articles: BlogArticle[]) => {
    const categories = articles
      .map(article => article.category || 'Articles Généraux')
      .filter((category, index, array) => array.indexOf(category) === index);
    return categories;
  };

  const getImageUrl = (imageUrl: string) => {
    if (imageUrl.startsWith('http')) {
      return imageUrl;
    }
    
    // Si l'URL commence par /img/pages, utiliser le domaine du frontend
    if (imageUrl.startsWith('/img/pages/')) {
      return `https://lapetiteacademie.ci${imageUrl}`;
    }
    
    // Si l'URL commence par /uploads/pages/, utiliser la nouvelle route API
    if (imageUrl.startsWith('/uploads/pages/')) {
      return `https://lapetiteacademie.ci/api/dynamic-pages/images/${imageUrl.split('/').pop()}`;
    }
    
    // Si l'URL commence par /uploads, utiliser le domaine du backend
    if (imageUrl.startsWith('/uploads/')) {
      return `https://lapetiteacademie.ci${imageUrl}`;
    }
    
    // Par défaut, ajouter le domaine du backend
    return `https://lapetiteacademie.ci${imageUrl}`;
  };

  const handleImageClick = (article: BlogArticle) => {
    // Rediriger vers la page de galerie des articles avec la vraie catégorie de l'article
    const articleCategory = article.category || 'Articles Généraux';
    console.log('🔍 Navigation vers galerie:', {
      articleTitle: article.title,
      articleCategory: articleCategory,
      url: `/blog-gallery/${encodeURIComponent(articleCategory)}`
    });
    navigate(`/blog-gallery/${encodeURIComponent(articleCategory)}`);
  };

  return (
    <Box sx={{
      bgcolor: 'white',
      py: { xs: 4, md: 6 },
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Motifs décoratifs en arrière-plan */}
      <Box sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundImage: 'url(/img/pages/doodles.png)', // Motifs décoratifs (fleurs, étoiles, lettres)
        backgroundSize: '200px',
        backgroundRepeat: 'repeat',
        opacity: 0.1,
        zIndex: 0
      }} />

      {/* Forme décorative verte sur le côté */}
      <Box sx={{
        position: 'absolute',
        right: 0,
        top: '10%',
        width: { xs: '80px', md: '150px' },
        height: { xs: '200px', md: '350px' },
        bgcolor: '#32CD32',
        borderRadius: '50% 0 0 50%',
        opacity: 0.8,
        zIndex: 1
      }} />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
        {/* Liste des articles - Une seule image par catégorie */}
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          maxWidth: { xs: '100%', md: '70%' } // Pour laisser de l'espace à la forme verte
        }}>
          {getUniqueCategories(articles).map((category, index) => {
            const firstArticle = articles.find(article => article.category === category) || articles[0];
            return (
            <Box
              key={index}
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                gap: { xs: 2, md: 3 },
                alignItems: 'flex-start',
                mb: { xs: 4, md: 5 }
              }}
            >
              {/* Image de l'article - Cliquable */}
              <Box 
                onClick={() => handleImageClick(firstArticle)}
                sx={{
                  width: { xs: '100%', md: '250px' },
                  height: { xs: 150, md: 150 },
                  flexShrink: 0,
                  overflow: 'hidden',
                  borderRadius: 2,
                  border: '2px solid #1780c2',
                  cursor: 'pointer',
                  position: 'relative',
                  transition: 'transform 0.3s ease, boxShadow 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: '0 8px 25px rgba(0,0,0,0.2)',
                    border: '2px solid #FF9800'
                  }
                }}
              >
                <img
                  src={getImageUrl(firstArticle.image_url)}
                  alt={firstArticle.alt_text || firstArticle.title}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    display: 'block'
                  }}
                  onError={(e) => {
                    // Image de fallback en cas d'erreur
                    e.currentTarget.src = `https://picsum.photos/400/300?random=${firstArticle.id}`;
                  }}
                />
                
                {/* Overlay au survol */}
                <Box sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  bgcolor: 'rgba(0, 0, 0, 0.3)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  opacity: 0,
                  transition: 'opacity 0.3s ease',
                  '&:hover': {
                    opacity: 1
                  }
                }}>
                  <PhotoLibraryIcon sx={{ color: 'white', fontSize: 40 }} />
                </Box>
              </Box>

              {/* Contenu de l'article */}
              <Box sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                gap: 2
              }}>
                {/* Titre */}
                <Typography
                  variant="h5"
                  sx={{
                    color: '#1780c2',
                    fontWeight: 700,
                    fontSize: { xs: 18, md: 20 },
                    fontFamily: 'Arial, sans-serif',
                    lineHeight: 1.3,
                    textDecoration: 'underline',
                    textDecorationColor: '#1780c2'
                  }}
                >
                  {firstArticle.title}
                </Typography>

                {/* Description */}
                <Typography
                  variant="body1"
                  sx={{
                    color: '#333',
                    fontSize: { xs: 14, md: 16 },
                    lineHeight: 1.6,
                    fontFamily: 'Arial, sans-serif'
                  }}
                >
                  {firstArticle.excerpt}
                </Typography>

                {/* Lien "Lire l'article complet" */}
                {firstArticle.content && firstArticle.content !== firstArticle.excerpt && (
                  <Button
                    component={RouterLink}
                    to={`/blog/${firstArticle.id}`}
                    sx={{
                      color: '#FF9800',
                      fontWeight: 600,
                      fontSize: { xs: 14, md: 16 },
                      textTransform: 'none',
                      textDecoration: 'underline',
                      textDecorationColor: '#FF9800',
                      alignSelf: 'flex-start',
                      p: 0,
                      minWidth: 'auto',
                      mt: 1,
                      '&:hover': {
                        bgcolor: 'transparent',
                        textDecoration: 'underline',
                        textDecorationColor: '#1780c2',
                        color: '#1780c2'
                      }
                    }}
                  >
                    Lire l'article complet
                  </Button>
                )}
              </Box>
            </Box>
            );
          })}
        </Box>
      </Container>
      
    </Box>
  );
};

// Section Actualités de l'établissement avec données dynamiques
const ActualitesSection = ({ news }: { news: SchoolNews[] }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();

  // Fonction pour obtenir les catégories uniques (pour les actualités, on utilise le titre comme catégorie)
  const getUniqueCategories = (news: SchoolNews[]) => {
    const categories = news
      .map(item => {
        // Extraire une catégorie basée sur le titre
        if (item.title.includes('Rentrée')) return 'Rentrée Scolaire';
        if (item.title.includes('Programme')) return 'Programme Activités';
        if (item.title.includes('Sortie')) return 'Sorties Éducatives';
        return 'Actualités Générales';
      })
      .filter((category, index, array) => array.indexOf(category) === index);
    return categories;
  };

  const getImageUrl = (imageUrl: string) => {
    if (imageUrl.startsWith('http')) {
      return imageUrl;
    }
    
    // Si l'URL commence par /img/pages, utiliser le domaine du frontend
    if (imageUrl.startsWith('/img/pages/')) {
      return `https://lapetiteacademie.ci${imageUrl}`;
    }
    
    // Si l'URL commence par /uploads/pages/, utiliser la nouvelle route API
    if (imageUrl.startsWith('/uploads/pages/')) {
      return `https://lapetiteacademie.ci/api/dynamic-pages/images/${imageUrl.split('/').pop()}`;
    }
    
    // Si l'URL commence par /uploads, utiliser le domaine du backend
    if (imageUrl.startsWith('/uploads/')) {
      return `https://lapetiteacademie.ci${imageUrl}`;
    }
    
    // Par défaut, ajouter le domaine du backend
    return `https://lapetiteacademie.ci${imageUrl}`;
  };

  const handleImageClick = (actualite: SchoolNews) => {
    // Rediriger vers la page de galerie des actualités
    console.log('🔍 Navigation vers galerie actualités:', {
      actualiteTitle: actualite.title,
      url: '/blog-gallery/actualites'
    });
    navigate('/blog-gallery/actualites');
  };

  return (
    <Box sx={{
      bgcolor: 'white',
      py: { xs: 4, md: 6 },
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Motifs décoratifs en arrière-plan */}
      <Box sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundImage: 'url(/img/pages/doodles.png)', // Motifs décoratifs scolaires
        backgroundSize: '200px',
        backgroundRepeat: 'repeat',
        opacity: 0.1,
        zIndex: 0
      }} />

      {/* Forme décorative verte en bas à gauche */}
      <Box sx={{
        position: 'absolute',
        left: 0,
        bottom: 0,
        width: { xs: '80px', md: '150px' },
        height: { xs: '80px', md: '120px' },
        bgcolor: '#32CD32',
        borderRadius: '0 50% 0 0',
        opacity: 0.8,
        zIndex: 1
      }} />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
        {/* Titre de la section */}
        <Typography
          variant="h2"
          sx={{
            color: '#1780c2',
            fontWeight: 700,
            fontSize: { xs: 24, md: 32, lg: 36 },
            textAlign: 'center',
            mb: 6,
            fontFamily: 'Arial, sans-serif'
          }}
        >
          Actualités de l'établissement
        </Typography>

        {/* Grille des actualités - Une seule image par catégorie */}
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { 
            xs: '1fr', 
            sm: 'repeat(2, 1fr)', 
            md: `repeat(${Math.min(getUniqueCategories(news).length, 3)}, 1fr)` 
          },
          gap: { xs: 4, md: 6 },
          alignItems: 'start'
        }}>
          {getUniqueCategories(news).map((category, index) => {
            const firstActualite = news.find(item => {
              if (category === 'Rentrée Scolaire') return item.title.includes('Rentrée');
              if (category === 'Programme Activités') return item.title.includes('Programme');
              if (category === 'Sorties Éducatives') return item.title.includes('Sortie');
              return true;
            }) || news[0];

            return (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center'
                }}
              >
                {/* Image de l'actualité - Cliquable */}
                <Box 
                  onClick={() => handleImageClick(firstActualite)}
                  sx={{
                    width: '100%',
                    height: { xs: 200, sm: 250, md: 300 },
                    overflow: 'hidden',
                    borderRadius: 2,
                    border: '2px solid #1780c2',
                    mb: 3,
                    position: 'relative',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    cursor: 'pointer',
                    textDecoration: 'none',
                    transition: 'transform 0.3s ease, boxShadow 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-5px)',
                      boxShadow: '0 8px 25px rgba(0,0,0,0.25)',
                      border: '2px solid #FF9800'
                    }
                  }}
                >
                  <img
                    src={getImageUrl(firstActualite.image_url)}
                    alt={firstActualite.alt_text || firstActualite.title}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      display: 'block'
                    }}
                    onError={(e) => {
                      // Image de fallback en cas d'erreur
                      e.currentTarget.src = `https://picsum.photos/400/300?random=${firstActualite.id}`;
                    }}
                  />
                  {/* Overlay au survol */}
                  <Box sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    bgcolor: 'rgba(0, 0, 0, 0.3)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    opacity: 0,
                    transition: 'opacity 0.3s ease',
                    '&:hover': {
                      opacity: 1
                    }
                  }}>
                    <PhotoLibraryIcon sx={{ color: 'white', fontSize: 40 }} />
                  </Box>
                </Box>

                {/* Titre de l'actualité */}
                <Typography
                  variant="h6"
                  sx={{
                    color: '#333',
                    fontWeight: 700,
                    fontSize: { xs: 16, md: 18 },
                    fontFamily: 'Arial, sans-serif',
                    lineHeight: 1.3,
                    textAlign: 'center',
                    mb: 2,
                    textDecoration: 'underline',
                    textDecorationColor: '#333'
                  }}
                >
                  {firstActualite.title}
                </Typography>

                {/* Description */}
                <Typography
                  variant="body2"
                  sx={{
                    color: '#666',
                    fontSize: { xs: 14, md: 15 },
                    lineHeight: 1.5,
                    textAlign: 'center',
                    fontFamily: 'Arial, sans-serif'
                  }}
                >
                  {firstActualite.description}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Container>
      
    </Box>
  );
};

// Composant principal Blog
const Blog = () => {
  const navigate = useNavigate();
  const [articles, setArticles] = useState<BlogArticle[]>([]);
  const [news, setNews] = useState<SchoolNews[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    const loadBlogContent = async () => {
      try {
        setLoading(true);
        
        // Charger les articles et les actualités en parallèle
        const [articlesResponse, newsResponse] = await Promise.all([
          axios.get('https://lapetiteacademie.ci/api/dynamic-pages/blog/articles'),
          axios.get('https://lapetiteacademie.ci/api/dynamic-pages/news')
        ]);
        
        if (articlesResponse.data.status === 'success') {
          setArticles(articlesResponse.data.data || []);
        }
        
        if (newsResponse.data.status === 'success') {
          setNews(newsResponse.data.data || []);
        }
      } catch (error) {
        console.error('Erreur lors du chargement du contenu du blog:', error);
        setError('Erreur lors du chargement du contenu');
        // Utiliser des données par défaut en cas d'erreur
        setArticles([
          {
            id: 1,
            title: "Les avantages des jeux éducatifs pour les enfants de la maternelle",
            excerpt: "L'éducation des tout-petits est une étape essentielle dans leur développement global. À la maternelle, les enfants découvrent le monde qui les entoure à travers le jeu, un outil naturel d'apprentissage.",
            content: "L'éducation des tout-petits est une étape essentielle dans leur développement global. À la maternelle, les enfants découvrent le monde qui les entoure à travers le jeu, un outil naturel d'apprentissage.\n\nLes jeux éducatifs offrent de nombreux avantages pour le développement des enfants de 3 à 5 ans. Ils stimulent la créativité, améliorent la concentration et renforcent les compétences sociales. À La Petite Académie, nous intégrons ces activités ludiques dans notre programme pédagogique pour favoriser l'épanouissement de chaque enfant.\n\nNos éducateurs utilisent des méthodes adaptées à chaque âge, en combinant apprentissage et plaisir. Les enfants développent ainsi leur autonomie, leur confiance en soi et leur curiosité naturelle pour le monde qui les entoure.",
            image_url: "/img/pages/53.jpg",
            alt_text: "Enfants jouant avec des jeux éducatifs",
            author: "La Petite Académie",
            is_published: true,
            is_featured: false,
            order_index: 1,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          },
          {
            id: 2,
            title: "Les bienfaits de la lecture pour les enfants de 3 à 5 ans",
            excerpt: "L'un des principaux avantages de la lecture est l'enrichissement du langage. À travers les histoires, les enfants découvrent de nouveaux mots, de nouvelles expressions et des structures grammaticales variées.",
            content: "L'un des principaux avantages de la lecture est l'enrichissement du langage. À travers les histoires, les enfants découvrent de nouveaux mots, de nouvelles expressions et des structures grammaticales variées.\n\nLa lecture développe également l'imagination et la créativité des enfants. Elle les aide à comprendre le monde qui les entoure et à développer leur empathie. À La Petite Académie, nous organisons des séances de lecture quotidiennes pour encourager cette passion chez nos élèves.\n\nNos bibliothèques sont équipées de livres adaptés à chaque âge, avec des illustrations colorées et des histoires captivantes. Les enfants apprennent ainsi à aimer les livres dès leur plus jeune âge.",
            image_url: "/img/pages/107.jpg",
            alt_text: "Enfants écoutant une histoire",
            author: "La Petite Académie",
            is_published: true,
            is_featured: false,
            order_index: 2,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          },
          {
            id: 3,
            title: "L'importance des activités en plein air pour les enfants de la maternelle",
            excerpt: "Le développement des enfants de maternelle ne se limite pas aux activités en classe ou aux jeux éducatifs.",
            content: "Le développement des enfants de maternelle ne se limite pas aux activités en classe ou aux jeux éducatifs. Les activités en plein air jouent un rôle crucial dans leur épanouissement physique et mental.\n\nÀ La Petite Académie, nous disposons d'espaces verts aménagés spécialement pour les enfants. Ces espaces permettent aux enfants de courir, sauter, grimper et explorer la nature en toute sécurité.\n\nLes activités extérieures favorisent le développement moteur, renforcent le système immunitaire et améliorent la concentration. Elles permettent également aux enfants de découvrir la biodiversité et de développer leur respect pour l'environnement.",
            image_url: "/img/pages/142.jpg",
            alt_text: "Enfants jouant en plein air",
            author: "La Petite Académie",
            is_published: true,
            is_featured: false,
            order_index: 3,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          }
        ]);
        
        setNews([
          {
            id: 1,
            title: "Rentrée scolaire 2025/2026",
            description: "Préparez-vous pour une nouvelle année scolaire riche en découvertes et apprentissages.",
            image_url: "/img/pages/MTC.jpeg",
            alt_text: "Préparation rentrée scolaire",
            order_index: 1,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          },
          {
            id: 2,
            title: "Programme des activités",
            description: "Découvrez notre programme d'activités éducatives et ludiques pour cette année.",
            image_url: "/img/pages/107.jpg",
            alt_text: "Programme activités scolaires",
            order_index: 2,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          },
          {
            id: 3,
            title: "Sortie découverte",
            description: "Nos sorties éducatives permettent aux enfants d'explorer le monde qui les entoure.",
            image_url: "/img/pages/65.jpg",
            alt_text: "Sortie éducative découverte",
            order_index: 3,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          }
        ]);
      } finally {
        setLoading(false);
      }
    };

    loadBlogContent();
  }, []);

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh' 
      }}>
        <Typography>Chargement...</Typography>
      </Box>
    );
  }

  return (
    <>
      <Helmet>
        <title>Blog Éducation et Actualités - La Petite Académie Abidjan</title>
        <meta name="description" content="Blog éducatif de La Petite Académie à Abidjan : articles sur l'éducation des enfants, jeux éducatifs, lecture, activités plein air, actualités de l'établissement. Conseils pédagogiques et informations sur notre crèche et maternelle bilingue." />
        <meta name="keywords" content="blog éducation Abidjan, articles éducation enfants, jeux éducatifs maternelle, lecture enfants, activités plein air, actualités école, conseils pédagogiques, La Petite Académie, crèche maternelle bilingue" />
        <meta property="og:title" content="Blog Éducation et Actualités - La Petite Académie Abidjan" />
        <meta property="og:description" content="Blog éducatif de La Petite Académie à Abidjan : articles sur l'éducation des enfants, jeux éducatifs, lecture, activités plein air, actualités de l'établissement." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://lapetiteacademie.ci/blog" />
        <link rel="canonical" href="https://lapetiteacademie.ci/blog" />
      </Helmet>
      <Box sx={{ 
        width: '100%',
        margin: 0,
        padding: 0
      }}>
        <ContactBanner />
        <NavigationBar />
        <BlogHeroSection />
        <BlogArticlesSection articles={articles} />
        <ActualitesSection news={news} />
        <SiteFooter />
      </Box>
    </>
  );
};

export default Blog;
